import React, { useState, useEffect, useRef } from "react";
import { RiPencilFill } from "react-icons/ri";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { MdBlock } from "react-icons/md";
import DropdownMenu from "../../../components/ui/DropdownMenu";

const ApprovalField = ({
  approvedAmount,
  actualAmount,
  onApproval,
  onRejection,
  approvalParams,
  isApproving = false,
  isRejecting = false,
  status,
}) => {
  const isRejected = status === "REJECTED";
  const [isEditing, setIsEditing] = useState(approvedAmount === null && !isRejected);
  const [editValue, setEditValue] = useState(approvedAmount || "0");

  // Track if approve was triggered
  const approveTriggeredRef = useRef(false);
  const prevIsApprovingRef = useRef(isApproving);

  // Update editValue when approvedAmount changes (after successful approval)
  useEffect(() => {
    if (approvedAmount !== null) {
      setEditValue(approvedAmount);
      // Don't set isEditing here; handled by isApproving transition
    }
  }, [approvedAmount]);

  // Exit editing mode when isApproving transitions from true to false after approve
  useEffect(() => {
    if (
      approveTriggeredRef.current &&
      prevIsApprovingRef.current &&
      !isApproving
    ) {
      setIsEditing(false);
      approveTriggeredRef.current = false;
    }
    prevIsApprovingRef.current = isApproving;
  }, [isApproving]);

  // Reset editing state if item becomes rejected
  useEffect(() => {
    if (isRejected) {
      setIsEditing(false);
    }
  }, [isRejected]);

  const handleSave = () => {
    if (onApproval && approvalParams && !isRejected) {
      approveTriggeredRef.current = true;
      onApproval({
        amount_approved: editValue,
        ...approvalParams,
      });
    }
  };

  const handleReject = () => {
    if (onRejection && approvalParams && !isRejected) {
      onRejection(approvalParams);
    }
  };

  const handleEdit = () => {
    if (!isRejected) {
      setIsEditing(true);
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "REVIEWED":
        return { text: "Approved", className: "text-green-600" };
      case "REJECTED":
        return { text: "Rejected", className: "text-red-600" };
      case "PARTIALLY_APPROVED":
        return { text: "Partially Approved", className: "text-green-600" };
        case "APPROVAL_AWAITING":
        return { text: "Approval Awaiting", className: "text-yellow-600" };
      default:
        return { text: "No Status", className: "text-red-600" };
    }
  };

  const statusDisplay = getStatusDisplay(status);

  // Dropdown menu items - only show reject if not already rejected
  const dropdownItems = !isRejected ? [
    {
      label: "Reject",
      onClick: handleReject,
      disabled: isApproving || isRejecting,
    },
  ] : [];

  // Display amount - show 0.00 if rejected, otherwise show approved amount
  const displayAmount = isRejected ? "0" : (approvedAmount || "0");

  return (
    <>
      {/* Approved Amount Column */}
      <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
        {(isEditing || approvedAmount === null) && !isRejected ? (
          <div className="grid place-items-center">
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="input text-sm w-full"
              disabled={isApproving || isRejecting || isRejected}
              min="0"
              placeholder="0"
            />
          </div>
        ) : (
          <div className="text-right">
            <p className="flex items-center justify-end space-x-1">
              <span className={`text-sm`}>
                ₹{parseFloat(displayAmount).toLocaleString()}
              </span>
              {approvedAmount !== null && !isRejected && (
                <button onClick={handleEdit} disabled={isApproving || isRejecting}>
                  <RiPencilFill className="w-4 h-4 text-neutral-600" />
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Actions Column */}
      <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] flex justify-center items-center space-x-1">
        {(isEditing || approvedAmount === null) && !isRejected ? (
          <>
            <button
              onClick={handleSave}
              disabled={isApproving || isRejecting}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {isApproving ? "Approving..." : "Approve"}
            </button>
            {dropdownItems.length > 0 && (
              <DropdownMenu
                trigger={
                  <button
                    className="bg-blue-600 text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                    disabled={isApproving || isRejecting}
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                }
                items={dropdownItems}
                disabled={isApproving || isRejecting}
                placement="bottom-end"
              />
            )}
          </>
        ) : (
          <>
            <p className={`text-sm ${statusDisplay.className}`}>
              {statusDisplay.text}
            </p>
            {!isRejected && dropdownItems.length > 0 && (
              <DropdownMenu
                trigger={
                  <button
                    className="text-neutral-600 hover:text-neutral-800 disabled:opacity-50"
                    disabled={isApproving || isRejecting}
                  >
                    <ChevronDownIcon className="w-5 h-5" />
                  </button>
                }
                items={dropdownItems}
                disabled={isApproving || isRejecting}
                placement="bottom-end"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ApprovalField;
